/**
 * The Schedule in one line, read before the controls: first drop → last
 * drop, the pace and the count, with the Danish times — and, under it,
 * where the count comes from (the drops of step 4) with a way there, and
 * the drops the country's window keeps from going (a hard limit). It
 * re-enters softly whenever an edit changes it. Danish first (15/10/2026):
 * the big line is the Danish clock, the country's times beside it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ArrowDown } from 'lucide-react';

import { dkLabel } from '../../../../utils/clock/dk-label';
import { minutesLabel } from '../../../../utils/format';
import { useCountryClock } from '../../../shared/time/country-clock-context';

interface Props {
  firstMin: number | null;
  lastMin: number | null;
  count: number;
  /** Drop times naming no linked group — they hold their time, nothing is sent. */
  noGroup: number;
  /** Drops of step 4 outside the country's window — not sent. */
  outside: number;
  /** "00:00–16:00". */
  windowLabel: string;
  every: number;
  /** Phase 33 — "every day" (said nothing of), "weekdays", "Fri–Sat"… */
  days: string;
  /** Phase 33 — "weekends 60 %" when a weekend day sends a share; null otherwise. */
  weekend: string | null;
  /** Opens step 4 (Drop slots) — where drops are added or removed. */
  onOpenDrops: () => void;
}

const OpenDrops: FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type="button" onClick={onClick} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
    Drop slots <ArrowDown className="h-3 w-3" />
  </button>
);

export const ScheduleHero: FC<Props> = ({ firstMin, lastMin, count, noGroup, outside, windowLabel, every, days, weekend, onOpenDrops }) => {
  const clock = useCountryClock();
  if ((count === 0 || firstMin === null || lastMin === null) && outside > 0)
    return <p className="text-sm font-medium text-warning">Every drop falls outside the window ({windowLabel}) — nothing is sent.</p>;
  if (count === 0 || firstMin === null || lastMin === null)
    return (
      <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
        No drops yet. <OpenDrops onClick={onOpenDrops} />
      </p>
    );
  const dkFirst = dkLabel(firstMin, clock.tz, clock.runDate);
  const dkLast = dkLabel(lastMin, clock.tz, clock.runDate);
  const local = `${minutesLabel(firstMin)} → ${minutesLabel(lastMin)}`;
  return (
    <div className="flex flex-col gap-1">
    <div
      key={`${firstMin}-${lastMin}-${count}-${noGroup}-${every}-${days}-${weekend}`}
      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 duration-300 animate-in fade-in-0 slide-in-from-bottom-1 motion-reduce:animate-none"
    >
      <span className="font-mono text-lg font-semibold tabular-nums tracking-tight text-foreground" data-testid="schedule-span">
        {dkFirst && dkLast ? `${dkFirst} → ${dkLast}` : local}
        {dkFirst && dkLast ? <span className="ml-1 text-sm" aria-label="Danish time">🇩🇰</span> : null}
      </span>
      <span className="text-sm text-muted-foreground">
        a drop every <b className="font-semibold text-foreground">{every} min</b> ·{' '}
        <b className="font-semibold text-foreground">
          {count} drop {count === 1 ? 'time' : 'times'}
        </b>
      </span>
      {days !== 'every day' || weekend ? (
        <span className="text-sm text-muted-foreground" data-testid="schedule-days">
          {days !== 'every day' ? (
            <>
              on <b className="font-semibold text-foreground">{days}</b>
            </>
          ) : null}
          {weekend ? `${days !== 'every day' ? ' · ' : ''}${weekend}` : null}
        </span>
      ) : null}
      {noGroup > 0 ? (
        <span className="text-xs font-medium text-warning">
          {noGroup} without a group — {noGroup === 1 ? 'it sends' : 'they send'} nothing
        </span>
      ) : null}
      {dkFirst && dkLast ? (
        <span className="font-mono text-xs tabular-nums text-muted-foreground" data-testid="schedule-span-country">
          {clock.flag} {local}
        </span>
      ) : null}
      <OpenDrops onClick={onOpenDrops} />
    </div>
      <OutsideWindow outside={outside} windowLabel={windowLabel} />
    </div>
  );
};

/** The window is a hard limit (user, 12/10/2026): drops outside it are listed but never sent. */
const OutsideWindow: FC<{ outside: number; windowLabel: string }> = ({ outside, windowLabel }) =>
  outside > 0 ? (
    <p className="text-xs font-medium text-warning">
      {outside} {outside === 1 ? 'drop falls' : 'drops fall'} outside the window ({windowLabel}) — not sent.
    </p>
  ) : null;
